import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";
import { EVENT_PRICES as P } from "../slugs/eventPricing";

const cannesFilmFestival: ChauffeurPage = {
  slug: "cannes-film-festival",
  title: "Private chauffeur Cannes Film Festival – VIP VTC",
  subtitle:
    "Private chauffeur during the Cannes Film Festival: red-carpet drop-offs, airport meet-and-greet and daily hire around the Palais des Festivals.",
  keywords: [
    "cannes film festival chauffeur",
    "cannes festival private chauffeur",
    "red carpet chauffeur cannes",
  ],
  hero: {
    title: "Private chauffeur during the Cannes Film Festival",
    subtitle:
      "Red carpet, screenings and parties: a dedicated chauffeur when the Croisette closes and Nice airport peaks.",
    keywords: [
      "Cannes Film Festival chauffeur",
      "red carpet VTC",
      "Palais des Festivals chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during the Cannes Film Festival",
    badges: ["VIP airport welcome", "Red-carpet drop-off", "Late-night runs"],
  },
  benefits: {
    heading: "Why a chauffeur during the Festival",
    items: [
      {
        title: "Croisette closures handled",
        description:
          "Security perimeters and road blocks around the Palais: the chauffeur knows the nearest authorised drop-off points.",
      },
      {
        title: "Airport at peak",
        description:
          "Nice Côte d'Azur is saturated in mid-May. Name-board welcome, flight tracking and a direct transfer to your Cannes hotel.",
      },
      {
        title: "Talent, press, industry",
        description:
          "Separate flows for guests, press teams and industry: punctual before screenings and conferences.",
      },
      {
        title: "Late-night runs secured",
        description:
          "After-parties on the Croisette, dinners in Mougins or Antibes: late return with a chauffeur who waits.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during the Cannes Film Festival",
    services: [
      {
        title: "Nice airport meet-and-greet",
        text: "Nice ↔ Cannes transfer with flight tracking during the peak arrivals of the fortnight.",
      },
      {
        title: "Red-carpet drop-off",
        text: "Discreet approach as close as possible to the Palais des Festivals, within the security perimeter and red-carpet timing.",
      },
      {
        title: "Full-day hire",
        text: "A dedicated chauffeur by the hour or day to chain screenings, meetings and photocalls.",
      },
      {
        title: "Evenings & after-parties",
        text: "Croisette hotels, private beaches and hinterland dinners, with a guaranteed late-night return.",
      },
      {
        title: "Team & press shuttles",
        text: "Multi-vehicle coordination for delegations, press officers and partners.",
      },
    ],
  },
  locations: {
    heading: "Key points during the Festival",
    items: [
      {
        name: "Palais des Festivals et des Congrès",
        description:
          "Red carpet, Grand Théâtre Lumière and the Marché du Film.",
      },
      {
        name: "La Croisette",
        description:
          "Palaces (Carlton, Martinez, Majestic) and private beaches.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description: "Terminals 1 and 2, peak arrivals of the fortnight.",
      },
      {
        name: "Mougins & hinterland",
        description: "Villas, dinners and parties outside the centre.",
      },
      {
        name: "Antibes / Cap d'Antibes",
        description: "Hotels and residences of talent and delegations.",
      },
    ],
  },
  generic: {
    heading: "The Cannes Film Festival from a chauffeur's seat",
    subheading:
      "Mid-May, twelve days when the Croisette closes and every minute counts before the red carpet.",
    paragraphs: [
      "Every May, the Cannes Film Festival transforms the Croisette: security perimeters, roads closed around the Palais des Festivals and dense pedestrian flows before each screening. A chauffeur who knows the authorised drop-offs gets you as close as possible without missing the red carpet.",
      "Arrivals concentrate at Nice Côte d'Azur airport over just a few days. With flight tracking and a name-board welcome, we absorb delays and the peaks of the fortnight to reach your hotel without waiting.",
      "The festival pace chains screenings, press conferences, photocalls and late nights. Hourly hire keeps the same chauffeur all day and secures the late-night returns after the after-parties.",
    ],
    bullets: [
      "Drop-off nearest the Palais",
      "Flight tracking in mid-May",
      "Returns past midnight",
    ],
  },
  process: {
    heading: "Booking a chauffeur for the Cannes Film Festival",
    steps: [
      "Tell us your fortnight dates, your hotel and your screening times.",
      "We plan the routes around the Croisette closures and the Palais perimeter.",
      "The chauffeur arrives in uniform, with flight tracking for your Nice arrivals.",
      "Direct transfer or hourly hire to cover red carpets, dinners and parties.",
    ],
  },
  faq: [
    {
      q: "How much does a private chauffeur cost during the Cannes Film Festival?",
      a: `Hourly hire starts at €${P.e3} for 3 hours in a Class E, €${P.v3} in a Class V (7 seats) and €${P.s3} in a Class S. A full 8-hour day costs €${P.e8}, €${P.v8} or €${P.s8} depending on the class, and each hour beyond the package is billed €${P.extraMin} to €${P.extraMax}. The Nice airport ⇄ Cannes transfer is a fixed price: €${P.niceE}, €${P.niceV} or €${P.niceS}. These are our public rates, and the final price is confirmed in writing before the service.`,
    },
    {
      q: "Transfer or hourly hire: which one for the Festival?",
      a: `A transfer is enough to arrive and leave: one journey, a price fixed in advance, from €${P.niceE} from Nice airport. For a day of screenings, meetings and a party, hourly hire keeps the same chauffeur and car, waiting for you during the screening, so you no longer book each journey. It starts at 3 hours.`,
    },
    {
      q: "How do I book a private chauffeur during the Cannes Film Festival?",
      a: "Book as early as possible: in mid-May, demand in Cannes is extreme. Share your dates, your Croisette hotel and your screening times so we can secure a dedicated chauffeur across the whole fortnight.",
    },
    {
      q: "Can you drop me as close as possible to the red carpet?",
      a: "Yes. During the Festival, the roads around the Palais des Festivals are closed and secured. The chauffeur drops you at the nearest authorised point within the perimeter and red-carpet timing, then collects you on the way out.",
    },
    {
      q: "Do you handle Nice airport arrivals at the height of the fortnight?",
      a: "Yes, with flight tracking and a name-board welcome. Nice Côte d'Azur airport is saturated during the Festival; we anticipate the arrival peaks and adjust the pick-up time if your flight is delayed.",
    },
    {
      q: "Do you cover late returns after the parties?",
      a: "Yes. Croisette after-parties and hinterland dinners end late: the chauffeur waits and guarantees your late-night return with complete peace of mind.",
    },
  ],
  relatedLinks: [
    {
      label: "Book a Cannes ⇄ Nice Airport transfer",
      href: "/en/chauffeur/transfers/cannes-nice-airport",
    },
    {
      label: "Book a Cannes ⇄ Monaco transfer",
      href: "/en/chauffeur/transfers/cannes-monaco",
    },
    {
      label: "Check availability",
      href: "/en/reservation",
    },
  ],
  cta: {
    title: "Your chauffeur for the Cannes Film Festival",
    text: "Airport welcome, red carpet and parties: book a dedicated chauffeur for the fortnight.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: `Cannes Film Festival chauffeur from €${P.e3} for 3 hours | KS Rent Services`,
    description: `Cannes Film Festival chauffeur: hourly hire from €${P.e3} for 3 hours, 8-hour day from €${P.e8}, Nice airport transfer from €${P.niceE}. Red-carpet drop-off.`,
    canonical: "/en/chauffeur/cannes-film-festival",
  },
};

export default cannesFilmFestival;
